import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Formik, Form } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCheckDouble,
  FaPlusCircle,
  FaUser,
  FaTrash,
  FaHistory,
  FaCreditCard,
  FaWallet,
  FaHandHoldingUsd,
  FaCommentAlt,
  FaSearch,
} from "react-icons/fa";
import { RiDiscountPercentLine, RiFileList3Line } from "react-icons/ri";
import toast from "react-hot-toast";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import SwitchButtons from "../../../shared/SwitchButtons";
import DropdownField from "../../../shared/DropdownField";
import TextInput from "../../../shared/TextInput";
import BillSummary from "./BillSummary";
import {
  actPriorityOptions,
  cardLabels,
  getCardOptions,
  getCardCost,
  patientCashPlansOptionsFormat,
  patientInsuracnePlansOptionsFormat,
  paymentMethods,
  practionerOptionsFormat,
  visitTypeOptions,
  getFixedCardCost,
} from "../../../constants/VisitsConstants";
import { createVisitValidationSchema } from "../../../validations/VisitsValidations";
import VisitPriceItems from "./VisitPriceItems";
import CustomButton from "../../../shared/CustomButton";
import {
  createNewVisit,
  getCashPayersData,
} from "../../../redux/slices/VisitsSlice";
import {
  getAppointmentItems,
  getLimitedPractitionerList,
  getPriceListByPractitionerId,
  handleCheckPatientElibility,
} from "../../../services/ApiMethods";
import CheckPermission from "../../../hooks/CheckPermission";
import {
  calculateCashItemsDetails,
  calculateInsuranceItemsDetails,
  getApplicableInsuranceLimit,
} from "./VisitItemsCalculations";
import { useTranslation } from "react-i18next";
import { IoTimeOutline } from "react-icons/io5";
import { fetchData } from "../../../helpers/fetchData";
import SwitchInput from "../../../shared/SwitchInput";
import Spinner from "../../../shared/Spinner";
import { RiEditCircleFill } from "react-icons/ri";
import { MdCancel, MdCheckCircle, MdOutlineClose } from "react-icons/md";
import DatePicker from "../../../shared/DatePicker";
import ReusableModal from "../../../shared/ReuseableModal";
import ApplyDiscountModal from "./ApplyDiscountModal";
import PatientListModal from "./PatientListModal";
import { EligibilityDataComponent } from "../../../pages/HIS/CreateWithoutSlot";
import { HiShieldCheck } from "react-icons/hi";
import Flag from "react-world-flags";
import { getCountryCode } from "../../../utils/CountryCodes";
import { CalculateAge } from "../../../utils/CalculateAge";
import PatientAlertsDisplay from "../patient-alerts/PatientAlertsDisplay";
import AddReferralModal from "./AddReferralModal";
import ReferralEligibilitySummary from "./ReferralEligibilitySummary";
import RemMaxLimit from "./RemMaxLimit";

const isDepositPaymentMethod = (paymentMethod) =>
  paymentMethod?.toLowerCase() === "deposit";

const withoutVatForDeposit = (values) =>
  isDepositPaymentMethod(values?.paymentMethod)
    ? {
        ...values,
        items: values?.items?.map((item) => ({
          ...item,
          tax_check: false,
          payer_vat: false,
        })),
      }
    : values;

const CreateVisitWithoutSlot = ({
  setEligibilityData,
  setSelectedPatient,
  visit_category,
  openDepositForm,
  selectedPatient,
  refreshCreditTrigger,
  refreshDepositTrigger,
  setOpenDepositForm,
  setOpenWithdrawDepositForm,
  setOpenPayCreditForm,
  setShowComments,
  showComments,
  eligibilityData,
  handlePatientSelectMain,
  selctedPatient,
  setSelctedPatient,
  patientDoc,
  setPatientDoc,
  orderData,
  setOpenVisit,
}) => {
  const { t, i18n } = useTranslation();
  const formRef = useRef(null);
  const slotsContainerRef = useRef(null);
  const lastFollowUpCheckKeyRef = useRef("");
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cardOptions, setCardOptions] = useState([]);
  const { hasPermission } = CheckPermission();
  const [followUp, setFollowUp] = useState(null);
  const [episodeRemainingLimit, setEpisodeRemainingLimit] = useState(0);
  const [episodeMaxLimit, setEpisodeMaxLimit] = useState(0);
  const [followUpPractitionerName, setFollowUpPractitionerName] = useState("");
  const [followUpSpecialityName, setFollowUpSpecialityName] = useState("");
  const { cashPayers } = useSelector((state) => state?.visits);
  const cashPayerId = cashPayers?.[0]?.payer_id;
  const followUpPatientTypes = ["cash", "insurance", "corporate", "government"];
  const { auth } = useSelector((state) => state);
  const isAdmin = auth?.user?.role?.name_en?.toLowerCase() === "admin";
  const isManager = useSelector(
    (state) => state.auth.user?.role?.name_en == "manager",
  );
  const isAccountant =
    auth?.user?.role?.name_en?.toLowerCase() === "accountant";
  const isReceptionist =
    auth?.user?.role?.name_en?.toLowerCase() === "receptionist";
  const businessId = auth?.user?.business_id;
  const preSelectedPatientData = state?.patientDetails || null;
  const isCreatedFromAppointment = state?.isCreatedFromAppointment || false;
  const {
    day = "",
    start_time = "",
    date = "",
    selectedPractitionerId = "",
    appointment_id = "",
    end_time = "",
  } = state ?? {};
  const [patientType, setPatientType] = useState(
    orderData?.patient?.beneficiary_type?.toLowerCase() || "cash",
  );
  const [selectedPatientData, setSelectedPatientData] = useState(
    orderData?.patient || preSelectedPatientData || null,
  );
  const [payemntStatus, setPaymentStatus] = useState(
    state?.payment_status || "paid",
  );
  const [depositLoading, setDepositLoading] = useState(false);
  const [masterPriceCheck, setMasterPriceCheck] = useState(false);
  const [masterPriceVat, setMasterPriceVat] = useState(false);
  const [depositBalance, setDepositBalance] = useState(0);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [referralEligible, setReferralEligible] = useState(false);
  const [openReferralModal, setOpenReferralModal] = useState(false);
  const [patientInsuracnePlans, setPatientInsurancePlans] = useState([]);
  const [practitionersList, setPractionersList] = useState([]);
  const [selectedPayerData, setSelectedPayerData] = useState(null);
  const [selectedInurancePlan, setSelectedInsurancePlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  // const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  // const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [slotsData, setSlotData] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isAppointment, setIsAppointment] = useState(false);
  const [slotDay, setDay] = useState();
  const [visitDate, setVisitDate] = useState(
    moment(new Date(), "YYYY-MM-DD").toDate(),
  );

  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [checkDataLoading, setCheckDataLoading] = useState(false);
  const isHasPractitionerPermission = hasPermission("practitioner", "get");
  const isHaveGetPayerPermission = hasPermission("payer", "get");
  const [episodeType, setEpisodeType] = useState("");
  const [episodeId, setEpisodeId] = useState("");
  const [visitReference, setVisitReference] = useState(0);
  const isHaveGetPricelistPermission = hasPermission("pricelists", "get");
  const [speciality, setSpeciality] = useState("");
  const [openListModal, setOpenListModal] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [listModalData, setListModalData] = useState([]);
  const [contactPagination, setContactPagination] = useState({
    page: 1,
    perPage: 10,
  });
  const [department, setDepartment] = useState("");
  const [practitionerData, setPractitionerData] = useState(null);
  const [forceRefreshTrigger, setForceRefreshTrigger] = useState(0);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [invoiceDiscount, setInvoiceDiscount] = useState(0);
  const [practitionerPriceListPayload, setPriatitionerPricePayload] = useState({
    price_list_type_id: "67a2f54d664671bae04c678c",
    payer_id: null,
    practitioner_id: selectedPractitionerId,
    payment_type: patientType,
    visitType: "Consultation",
  });

  useEffect(() => {
    if (!startTime || !endTime) return;

    const scrollTimer = setTimeout(() => {
      const slotButtons =
        slotsContainerRef.current?.querySelectorAll("[data-slot-key]");
      const selectedSlotButton = Array.from(slotButtons || []).find(
        (button) => button.dataset.slotKey === `${startTime}-${endTime}`,
      );
      selectedSlotButton?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }, 0);

    return () => clearTimeout(scrollTimer);
  }, [startTime, endTime, slotsData]);

  const onSelectOrder = (order) => {
    setOpenVisit(false);
    if (order) {
      setSelectedPatientData(order.patient);
      setPatientType(order.patient?.beneficiary_type?.toLowerCase() || "cash");
      formRef.current.setValues({
        ...formRef.current.values,
        patientName: order.patient?.full_name || "",
        patientDocumentId: order.patient?.document_id || "",
        patientFileNo: order.patient?.file_no || "",
        patient_id: order.patient?.patient_id || "",
        contactNo: order.patient?.contact || "",
        items: order.items || [],
        practitioner: order.practitioner_id || "",
        speciality: order.practitioner?.speciality?.name_en || "",
        comment: order.comment || "",
        patientType: order.patient?.beneficiary_type?.toLowerCase() || "cash",
        order_id: order.order_id || order._id || "",
      });

      if (order.patient?.insurance_plans?.length > 0) {
        setPatientInsurancePlans(order.patient.insurance_plans);
        setSelectedPayerData(order.patient.insurance_plans[0]);
      }
    }
  };

  const memoizedPatientData = useMemo(
    () => ({
      ...selectedPatientData,
      patientType,
    }),
    [selectedPatientData, patientType],
  );

  const initialValues = {
    isCreatedFromAppointment: isCreatedFromAppointment ? true : false,
    start_time: start_time,
    end_time: end_time,
    appointment_id: appointment_id || "",
    visit_date: orderData?.visit_date
      ? moment(orderData?.visit_date, "YYYY-MM-DD").toDate()
      : moment(new Date(), "YYYY-MM-DD").toDate(),
    patientName:
      orderData?.patient?.full_name || preSelectedPatientData?.full_name || "",
    patientDocumentId:
      orderData?.patient?.document_id ||
      preSelectedPatientData?.document_id ||
      state?.doc_id ||
      selectedPatient?.document_id ||
      "",
    patientFileNo:
      orderData?.patient?.file_no ||
      preSelectedPatientData?.file_no ||
      selectedPatient?.file_no ||
      "",
    patientType:
      orderData?.patient?.beneficiary_type?.toLowerCase() ||
      preSelectedPatientData?.beneficiary_type?.toLowerCase() ||
      "cash",
    practitioner: orderData?.practitioner_id || selectedPractitionerId || "",
    payerName: getSelectedPayerData() || "",
    speciality: orderData?.speciality || "",
    visitType: orderData?.visit_type || "Consultation",
    paymentMethod:
      depositBalance > 0 ? "Deposit" : orderData?.payment_method || "card",
    priority: orderData?.priority || "el",
    comment: orderData?.comment || "",
    contactNo:
      orderData?.patient?.contact || preSelectedPatientData?.contact || "",
    items: orderData?.items || [],
    paid: payemntStatus,
    cardType: depositBalance > 0 ? "" : "Mada",
    cards: depositBalance > 0 ? [] : [{ cardType: "Mada", amount: 0 }],
    deduct_from_deposit: false,
    auth_ref: "",
    valid_until: "",
    order_id: orderData?.order_id || orderData?._id || "",
  };

  useEffect(() => {
    if (orderData?.patient) {
      if (orderData.patient.insurance_plans?.length > 0) {
        setPatientInsurancePlans(orderData.patient.insurance_plans);
        setSelectedPayerData(orderData.patient.insurance_plans[0]);
      }
    } else if (selectedPatientData?.insurance_plans?.length > 0) {
      setPatientInsurancePlans(selectedPatientData.insurance_plans);
      setSelectedPayerData(selectedPatientData.insurance_plans[0]);
    }
  }, [orderData, selectedPatientData]);

  useEffect(() => {
    if (practitionerData) {
      getCardOptions(setCardOptions, practitionerData);
    }
  }, [practitionerData]);

  useEffect(() => {
    handleSetFieldValue("patientType", patientType);
    if (selectedPatientData && formRef?.current?.values?.items?.length) {
      formRef?.current?.setFieldValue("payerName", "");
      formRef?.current?.setFieldValue("items", []);
    }
    if (patientType == "cash") {
      formRef?.current?.setFieldValue("payerName", cashPayers?.[0]?.payer_id);
      setSelectedPayerData(cashPayers?.[0]);
      setPriatitionerPricePayload((prev) => ({
        ...prev,
        payer_id: cashPayers?.[0]?.payer_id,
        payment_type: "cash",
      }));
    } else {
      if (selectedPatientData) {
        const firstPlan = selectedPatientData?.insurance_plans?.[0];
        const planId = firstPlan?.his_payer_id || firstPlan?.payer_id;
        formRef?.current?.setFieldValue("payerName", planId);
        setPriatitionerPricePayload((prev) => ({
          ...prev,
          payer_id: planId,
          payment_type: patientType,
        }));
        setSelectedPayerData(firstPlan);
        setSelectedInsurancePlan(firstPlan);
      } else {
        formRef?.current?.setFieldValue("payerName", "");
        setSelectedPayerData(null);
        setSelectedInsurancePlan(null);
      }
    }
  }, [patientType]);

  useEffect(() => {
    if (patientType !== "cash" || !cashPayerId) return;

    formRef?.current?.setFieldValue("payerName", cashPayerId);
    setSelectedPayerData(cashPayers?.[0]);
    setPriatitionerPricePayload((prev) => ({
      ...prev,
      payer_id: cashPayerId,
      payment_type: "cash",
    }));
    setSelectedInsurancePlan(null);
  }, [patientType, cashPayerId]);

  useEffect(() => {
    if (practitionerData && selectedPatientData) {
      const shouldWaitForEligibility =
        patientType === "insurance" && !eligibilityChecked && !referralEligible;

      if (!shouldWaitForEligibility) {
        setFollowUp(null);
        checkFollowUp(selectedPatientData, patientType);
      }
    }
  }, [
    selectedPatientData,
    practitionerData,
    selectedPayerData,
    cashPayerId,
    patientType,
    eligibilityChecked,
    referralEligible,
  ]);

  useEffect(() => {
    handleSetFieldValue("paid", payemntStatus);
  }, [payemntStatus]);

  const handleSetFieldValue = (field, value) => {
    const currentValues = formRef?.current?.values || {};
    formRef?.current?.setValues({
      ...currentValues,
      [field]: value,
    });
  };

  useEffect(() => {
    if (isHasPractitionerPermission) {
      getLimitedPractitionerList(setFieldsLoading, setPractionersList);
    }
  }, [isHasPractitionerPermission]);

  useEffect(() => {
    if (isHaveGetPayerPermission && !cashPayers) {
      dispatch(getCashPayersData());
    }
  }, [isHaveGetPayerPermission]);

  const checkFollowUp = async (
    patient = selectedPatientData,
    patientTypeOverride = patientType,
  ) => {
    const normalizedPatientType = patientTypeOverride?.toLowerCase();
    if (
      !followUpPatientTypes.includes(normalizedPatientType) ||
      !patient?.patient_id ||
      !practitionerData?.practitioner_id
    ) {
      return;
    }

    const visitDateForCheck = moment(visitDate).format("YYYY-MM-DD");
    const payerId =
      normalizedPatientType === "cash"
        ? cashPayerId || formRef?.current?.values?.payerName || ""
        : selectedPayerData?.his_payer_id ||
          selectedPayerData?.payer_id ||
          formRef?.current?.values?.payerName ||
          "";

    if (!payerId) return;

    const followUpCheckKey = [
      patient?.patient_id,
      practitionerData?.practitioner_id,
      practitionerData?.speciality?.id,
      patient?.file_no,
      normalizedPatientType,
      visitDateForCheck,
      payerId,
    ].join("|");

    if (lastFollowUpCheckKeyRef.current === followUpCheckKey) return;

    lastFollowUpCheckKeyRef.current = followUpCheckKey;
    setCheckDataLoading(true);
    try {
      const res = await fetchData("POST", "/v3/patient/checkFollowup", {
        patient_id: patient?.patient_id,
        practitioner_id: practitionerData?.practitioner_id,
        speciality_id: practitionerData?.speciality?.id,
        file_number: patient?.file_no,
        patient_type: normalizedPatientType,
        visit_date: visitDateForCheck,
        payer_id: payerId,
      });
      if (res?.data) {
        setFollowUp(res?.data?.visit_type);
        setVisitReference(res?.data?.last_first_visit_id || 0);
        setEpisodeRemainingLimit(res?.data?.episode_remaining_limit || 0);
        setEpisodeMaxLimit(res?.data?.episode_max_limit || 0);
        if (
          res?.data?.visit_type === "Follow Up" ||
          res?.data?.visit_type === "Follow up"
        ) {
          formRef?.current?.setFieldValue("visitType", "Follow Up");
          setEpisodeId(res?.data?.episode_id);
          setEpisodeType(res?.data?.visit_type);
          setFollowUpPractitionerName(res?.data?.practitioner_name || "");
          setFollowUpSpecialityName(res?.data?.speciality_name || "");
        } else {
          setEpisodeId("");
          setEpisodeType("");
          setFollowUpPractitionerName("");
          setFollowUpSpecialityName("");
          formRef?.current?.setFieldValue("visitType", "Consultation");
        }
      } else {
        setFollowUp("New Visit");
        setVisitReference(0);
        setEpisodeRemainingLimit(0);
        setFollowUpPractitionerName("");
        setFollowUpSpecialityName("");
        formRef?.current?.setFieldValue("visitType", "Consultation");
      }
      setForceRefreshTrigger((prev) => prev + 1);
      return res?.data;
    } catch (error) {
      lastFollowUpCheckKeyRef.current = "";
      throw error;
    } finally {
      setCheckDataLoading(false);
    }
  };

  const handlePatientSelect = (
    event,
    formkikProps,
    fieldName,
    fromSlot = false,
  ) => {
    setFollowUp(null);
    setEligibilityChecked(false);
    if (!fromSlot) {
      setIsAppointment(false);
      setAppointmentId(null);
    }
    const patientData = event?.value?.patientData || event;
    setSelectedPatient(patientData);
    formkikProps.setFieldValue("items", []);
    formkikProps.setFieldValue("patient_id", patientData?.patient_id);
    formkikProps.setFieldValue("document_type", patientData?.document_type);
    setSelectedPatientData(patientData);
    setPatientInsurancePlans([
      ...(patientData?.insurance_plans || []),
      // ...(cashPayers?.length ? [cashPayers[0]] : []),
    ]);

    if (
      patientData?.beneficiary_type?.toLowerCase() != "cash" &&
      patientData?.insurance_plans?.length
    ) {
      const firstPlan = patientData?.insurance_plans?.[0];
      const planId = firstPlan?.his_payer_id || firstPlan?.payer_id;
      formkikProps.setFieldValue("payerName", planId);
      setSelectedPayerData(firstPlan);
      setSelectedInsurancePlan(firstPlan);
      setPriatitionerPricePayload((prev) => ({
        ...prev,
        payer_id: planId,
        payment_type: patientData?.beneficiary_type?.toLowerCase(),
      }));
    } else if (patientData?.beneficiary_type?.toLowerCase() == "cash") {
      formkikProps.setFieldValue("payerName", cashPayers?.[0]?.payer_id);
      setSelectedPayerData(cashPayers?.[0]);
      setSelectedInsurancePlan(null);
      setPriatitionerPricePayload((prev) => ({
        ...prev,
        payer_id: cashPayers?.[0]?.payer_id,
        payment_type: "cash",
      }));
    } else {
      formkikProps.setFieldValue("payerName", "");
      setSelectedPayerData(null);
      setSelectedInsurancePlan(null);
    }
    setPatientType(patientData?.beneficiary_type?.toLowerCase());
    formkikProps.setFieldValue("patientDocumentId", patientData?.document_id);
    formkikProps.setFieldValue("patientFileNo", patientData?.file_no);
    formkikProps.setFieldValue("patientName", patientData?.full_name);
    formkikProps.setFieldValue(
      "patientType",
      patientData?.beneficiary_type?.toLowerCase(),
    );
    formkikProps.setFieldValue("contactNo", patientData?.contact);
    if (setEligibilityData) {
      setEligibilityData(null);
    }
    toast.dismiss("eligibility-check");
    if (practitionerData && !fromSlot) {
      const patientTypeToCheck = patientData?.beneficiary_type?.toLowerCase();
      const shouldWaitForEligibility =
        patientTypeToCheck === "insurance" && !eligibilityChecked;

      if (!shouldWaitForEligibility) {
        checkFollowUp(patientData, patientTypeToCheck);
      }
    }
    if (fieldName) {
      requestAnimationFrame(() => {
        const inputEl = document.querySelector(`[name="${fieldName}"]`);
        inputEl?.blur();
      });
    }
  };
  const handleChangePractitioner = (event, props) => {
    let value = event?.target?.value;
    props.setFieldValue("practitioner", value);
    setFollowUp(null);
    setIsAppointment(false);
    setAppointmentId(null);
    let selectedPractionerData = practitionersList?.find(
      (data) => data?.practitioner_id == value,
    );
    setPractitionerData(selectedPractionerData);
    setPriatitionerPricePayload((prev) => ({
      ...prev,
      practitioner_id: value,
    }));
    setSpeciality(selectedPractionerData?.speciality);
    if (selectedPractionerData) {
      props.setFieldValue(
        "speciality",
        selectedPractionerData?.speciality?.name_en,
      );
    }
    // Reset follow-up check key so checkFollowUp re-runs with the new practitioner
    lastFollowUpCheckKeyRef.current = "";
  };

  const handleChangeVisitType = (event, props) => {
    let value = event?.target?.value;
    props.setFieldValue("visitType", value);
    setFollowUp(value);
    if (value == "Follow Up") {
      props.setFieldValue("items", []);
    }
    setPriatitionerPricePayload((prev) => ({
      ...prev,
      visitType: value,
    }));
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      if (!startTime) {
        toast.error(t("Please select slot to create visit"));
        return;
      }
      const hasOutOfStockItem = values?.items?.some(
        (item) => item?.out_of_stock,
      );
      // if (hasOutOfStockItem) {
      //   toast.error('Please remove out of stock item first')
      //   return
      // }

      const billableItems =
        values?.items?.filter(
          (item) => !item?.low_stock && !item?.out_of_stock,
        ) || [];
      const billableValues = {
        ...values,
        items: billableItems,
      };
      const payloadValues = withoutVatForDeposit(billableValues);

      if (!billableItems?.length) {
        toast.error(t("At least one item is required"));
        return;
      }

      if (!values?.appointment_id && isCreatedFromAppointment) {
        toast.error("Appointment ID is missing.");
        return;
      }
      if (values?.paymentMethod === "multi_payment") {
        const cashDetails = calculateCashItemsDetails(payloadValues);

        const insuranceDetails = calculateInsuranceItemsDetails(
          payloadValues,
          getApplicableInsuranceLimit(
            selectedPayerData,
            episodeRemainingLimit,
            episodeMaxLimit,
          ),
          selectedPatientData,
        );

        let netCashPay = Number(
          (cashDetails?.totals?.netPayableAmount || 0) +
            (cashDetails?.totals?.patientTaxTotal || 0),
        );

        let netInsurancePay = Number(
          (insuranceDetails?.totals?.patientShareTotal || 0) +
            (insuranceDetails?.totals?.patientTaxTotal || 0),
        );

        const totalNetPay =
          values?.patientType != "cash" ? netInsurancePay : netCashPay;

        const inputCash = Number(values?.cash || 0);
        const inputBank = Number(values?.bank || 0);
        const inputCredit = Number(values?.credit || 0);
        const inputCards =
          values.cards?.reduce(
            (sum, card) => sum + (Number(card.amount) || 0),
            0,
          ) || 0;

        const totalInput = inputCash + inputBank + inputCredit + inputCards;

        if (Math.abs(totalNetPay - totalInput) > 0.05) {
          toast.error(
            `Total payment (${totalInput?.toFixed(
              2,
            )}) must match the visit amount (${totalNetPay?.toFixed(2)})`,
          );
          return;
        }
      }
      const currentTime = moment().format("HH:mm");
      const formattedVisitDate = `${moment(values?.visit_date).format(
        "YYYY-MM-DD",
      )}T${currentTime}`;
      const prioritySelected = actPriorityOptions(t).find(
        (option) => option.value === values?.priority,
      );
      const cardCostValue = getFixedCardCost(cardOptions, values?.cardType);

      const itemsDetails =
        values?.patientType == "cash"
          ? calculateCashItemsDetails(payloadValues, cardCostValue)
          : calculateInsuranceItemsDetails(
              payloadValues,
              getApplicableInsuranceLimit(
                selectedPayerData,
                episodeRemainingLimit,
                episodeMaxLimit,
              ),
              selectedPatientData,
              cardCostValue,
            );

      const netCashPay = Number(
        (itemsDetails?.totals?.patientShareTotal || 0) +
          (itemsDetails?.totals?.patientTaxTotal || 0),
      );

      const netInsurancePay = Number(
        (itemsDetails?.totals?.patientShareTotal || 0) +
          (itemsDetails?.totals?.patientTaxTotal || 0),
      );

      if (values?.deduct_from_deposit && depositBalance < netCashPay) {
        return toast.error(
          `Deposit balance is insufficient. Available deposit balance is ${depositBalance.toFixed(
            2,
          )}`,
        );
      }

      const totalAmount = (
        values?.patientType !== "cash" ? netInsurancePay : netCashPay
      ).toFixed(2);

      if (values?.paymentMethod === "multi_payment") {
        const inputCash = Number(values?.cash || 0);
        const inputBank = Number(values?.bank || 0);
        const inputCredit = Number(values?.credit || 0);
        const inputCards =
          values.cards?.reduce(
            (sum, card) => sum + (Number(card.amount) || 0),
            0,
          ) || 0;

        const totalInput = inputCash + inputBank + inputCredit + inputCards;

        if (Math.abs(totalAmount - totalInput) > 0.05) {
          toast.error(
            `Total payment (${totalInput?.toFixed(
              2,
            )}) must match the visit amount (${totalAmount})`,
          );
          return;
        }
      }

      const payload = {
        ...(referralEligible && {
          referral: {
            auth_ref: values.auth_ref,
            valid_until: values.valid_until,
          },
        }),
        order_id: values?.order_id,
        deduct_from_deposit: values?.deduct_from_deposit,
        visit_payment_type: values?.patientType,
        priority: {
          coding: [
            {
              system:
                "http://terminology.hl7.org/CodeSystem/encounter-priority",
              code: prioritySelected?.value,
              display: prioritySelected?.label,
            },
          ],
          text: prioritySelected?.text,
        },
        appointment_id: appointmentId,
        visit_type: { type: values?.visitType },
        contactNo: values?.contactNo,
        visit_date: formattedVisitDate,
        day: slotDay,
        start_time: startTime,
        end_time: endTime,
        patient_id: selectedPatientData?.patient_id,
        speciality,
        department,
        ...(episodeId && { episode_id: episodeId }),
        ...(episodeType && { episode_type: episodeType }),
        visit_reference: String(visitReference),
        practitioner_id: practitionerData?.practitioner_id,
        patient: selectedPatientData,
        payment_method: values?.paymentMethod,
        ...(values?.paymentMethod === "multi_payment" && {
          payment_detail: [
            ...(Number(values?.cash) > 0
              ? [
                  {
                    payment_method: "cash",
                    amount: values.cash,
                  },
                ]
              : []),

            ...(Number(values?.bank) > 0
              ? [
                  {
                    payment_method: "bank",
                    amount: values.bank,
                  },
                ]
              : []),

            ...(Number(values?.credit) > 0
              ? [
                  {
                    payment_method: "credit",
                    amount: values.credit,
                  },
                ]
              : []),

            ...(values.cards
              ?.filter((card) => Number(card.amount) > 0)
              ?.map((card) => ({
                payment_method: card.cardType,
                amount: card.amount,
                card_cost: getCardCost(cardOptions, card.cardType, card.amount),
              })) || []),
          ],
        }),
        ...(values?.paymentMethod == "card" && {
          payment_detail: [
            {
              payment_method: values?.cardType,
              amount: Number(totalAmount),
              card_cost: getCardCost(
                cardOptions,
                values?.cardType,
                totalAmount,
              ),
            },
          ],
        }),
        payment_status: values?.paid == "paid" ? "Paid" : "UnPaid",
        visit_category: visit_category,
        comment: values?.comment,
        items: payloadValues.items
          .map((item) => ({
            ...item,
            discount_amount: Number(item?.discount_amount),
            quantity: Number(item?.quantity),
            unit_price: Number(item?.unit_price),
            patientShareAmount: Number(item?.patient_share),
            patient_share: String(item?.patient_share),
            status: values?.paid == "paid" ? "Paid" : "UnPaid",
            ...(item?.is_approval && {
              approved_qty: Number(item?.approved_qty) || 0,
              approved_amount: Number(item?.approved_amount) || 0,
            }),
          }))
          .map(
            ({
              low_stock,
              out_of_stock,
              is_stock,
              stock,
              manage_costs,
              ...item
            }) => item,
          ),
      };

      const commonInvoicePayload = {
        date: formattedVisitDate,
        paid: values?.paid == "paid" ? true : false,
        invoice_ref: 0,
        description: values?.comment,
        item_details: itemsDetails,
        customer_id: selectedPatientData?.patient_id,
        payment_method: values?.paymentMethod,
        ...(values?.paymentMethod == "multi_payment" && {
          payment_detail: [
            ...(Number(values?.cash) > 0
              ? [
                  {
                    payment_method: "cash",
                    amount: values.cash,
                  },
                ]
              : []),

            ...(Number(values?.bank) > 0
              ? [
                  {
                    payment_method: "bank",
                    amount: values.bank,
                  },
                ]
              : []),

            ...(Number(values?.credit) > 0
              ? [
                  {
                    payment_method: "credit",
                    amount: values.credit,
                  },
                ]
              : []),

            ...(values.cards
              ?.filter((card) => Number(card.amount) > 0)
              ?.map((card) => ({
                payment_method: card.cardType,
                amount: card.amount,
                card_cost: getCardCost(cardOptions, card.cardType, card.amount),
              })) || []),
          ],
        }),
        ...(values?.paymentMethod == "card" && {
          payment_detail: [
            {
              payment_method: values?.cardType,
              amount: Number(totalAmount),
              card_cost: getCardCost(
                cardOptions,
                values?.cardType,
                totalAmount,
              ),
            },
          ],
        }),
        tax_type: "percentage",
        tax_amount: 15,
      };

      const invoicePayload =
        values?.patientType === "cash"
          ? {
              ...commonInvoicePayload,
              invoice_type: "sales",
              payer_id: selectedPayerData?.payer_id,
              selected_plan: selectedPayerData,
            }
          : {
              ...commonInvoicePayload,
              invoice_type: "credit",
              payer_id: selectedPayerData?.his_payer_id,
              selected_plan: selectedPayerData,
            };

      if (values?.paid == "paid") {
        payload.invoice_details = invoicePayload;
      }

      const creditPayload = {
        date: moment(values?.visit_date).format("YYYY-MM-DD"),
        patient_name: selectedPatientData?.full_name,
        patient_id: selectedPatientData?.patient_id,
        patient_document_id: selectedPatientData?.document_id,
        patient_file_no: selectedPatientData?.file_no,
        department_id: practitionerData?.department?.id,
        department_name: practitionerData?.department?.name_en,
        patient_credit: {
          patient_credit_amount:
            values?.paymentMethod == "credit"
              ? Number(totalAmount)
              : values?.paymentMethod == "multi_payment" &&
                  Number(values?.credit) > 0
                ? Number(values?.credit)
                : 0,
          remarks: values?.comments || "Credit Added",
        },
      };

      if (
        values?.paymentMethod === "card" ||
        values?.paymentMethod === "multi_payment"
      ) {
        if (values?.paymentMethod === "card") {
          const isValid =
            Array.isArray(cardOptions) &&
            cardOptions.some(
              (group) =>
                Array.isArray(group.options) &&
                group.options.some(
                  (opt) =>
                    opt.value === values.cardType ||
                    opt.label === values.cardType,
                ),
            );
          if (!values.cardType || !isValid) {
            toast.error("Please Select Card Type");
            return;
          }
        } else if (values?.paymentMethod === "multi_payment") {
          const cardPayments =
            values.cards?.filter((card) => Number(card.amount) > 0) || [];
          for (const card of cardPayments) {
            const isValid =
              Array.isArray(cardOptions) &&
              cardOptions.some(
                (group) =>
                  Array.isArray(group.options) &&
                  group.options.some(
                    (opt) =>
                      opt.value === card.cardType ||
                      opt.label === card.cardType,
                  ),
              );
            if (!card.cardType || !isValid) {
              toast.error("Please Select Card Type for all card payments");
              return;
            }
          }
        }
      }
      await dispatch(
        createNewVisit({ payload, navigate, withOutSlot: true, creditPayload }),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckElibility = async (payerData = selectedPayerData) => {
    if (patientType !== "insurance") return;
    if (!selectedPatientData || !payerData) {
      toast.error("Both Patient and Payer are required to check eligibility.");
      return;
    }
    formRef?.current?.setFieldValue("auth_ref", "");
    formRef?.current?.setFieldValue("valid_until", "");
    setReferralEligible(false);
    setEligibilityLoading(true);
    const toastId = "eligibility-check";

    try {
      const checkEligibilityPayload = {
        payer_id: payerData?.payer_id || payerData?.his_payer_id || "",
        rcm_ref: selectedPatientData?.rcm_ref || "",
        insurance_plan_id: payerData?.insurance_plan_id || payerData?.id || "",
        is_new_born: selectedPatientData?.is_new_born || false,
      };
      let eligibilityResponse = await handleCheckPatientElibility(
        checkEligibilityPayload,
      );

      if (!eligibilityResponse) {
        setEligibilityData(null);
        setPatientType("cash");
      } else {
        setEligibilityData(eligibilityResponse);

        const resource =
          eligibilityResponse?.nphies_entries?.CovElgResp?.resource;
        const siteEligibility =
          resource?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.code;
        const disposition = resource?.disposition;
        if (
          eligibilityResponse?.nphies_status == "error" ||
          siteEligibility?.toLowerCase() != "eligible"
        ) {
          setPatientType("cash");
        }
      }
      setEligibilityChecked(true);
    } catch (error) {
      setEligibilityData(null);
      setPatientType("cash");
      setEligibilityChecked(true);
    } finally {
      setEligibilityLoading(false);
    }
  };

  const handleAddReferral = async (values) => {
    formRef?.current?.setFieldValue("auth_ref", values.auth_ref.trim());
    formRef?.current?.setFieldValue("valid_until", values.valid_until);
    setEligibilityLoading(true);
    try {
      let payerData = selectedPayerData;
      let planData = selectedPayerData;
      const beneficiaryType =
        selectedPatientData?.beneficiary_type?.toLowerCase();

      if (
        patientType === "cash" &&
        beneficiaryType === "insurance" &&
        selectedPatientData?.insurance_plans?.length
      ) {
        const primaryPlan =
          selectedPatientData.insurance_plans.find((plan) => plan.is_primary) ||
          selectedPatientData.insurance_plans[0];
        payerData = primaryPlan;
        planData = primaryPlan;
      }

      const payerName = String(
        planData?.payer_name || planData?.name || planData?.name_en || "",
      )
        .replace(/\*/g, "")
        .trim()
        .toLowerCase();

      if (payerName === "bupa arabia for cooperative insurance") {
        setEligibilityData(null);
        setReferralEligible(true);
        setPatientType("insurance");
        setEligibilityChecked(true);
        toast.success(t("Patient marked as eligible through referral"));
        return;
      }

      const checkEligibilityPayload = {
        payer_id: payerData?.payer_id || payerData?.his_payer_id || "",
        rcm_ref: selectedPatientData?.rcm_ref || "",
        insurance_plan_id: planData?.insurance_plan_id || planData?.id || "",
        is_new_born: selectedPatientData?.is_new_born || false,
        is_referral: true,
        end_date: values.valid_until,
      };
      const eligibilityResponse = await handleCheckPatientElibility(
        checkEligibilityPayload,
      );

      if (!eligibilityResponse) {
        setEligibilityData(null);
        setReferralEligible(false);
        setPatientType("cash");
        toast.error(
          t("Referral eligibility check failed. Patient marked as cash."),
        );
      } else {
        setEligibilityData(eligibilityResponse);
        const resource =
          eligibilityResponse?.nphies_entries?.CovElgResp?.resource;
        const siteEligibility =
          resource?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.code;
        if (
          eligibilityResponse?.nphies_status == "error" ||
          siteEligibility?.toLowerCase() != "eligible"
        ) {
          setReferralEligible(false);
          setPatientType("cash");
          toast.error(t("Patient is not eligible through referral"));
        } else {
          setReferralEligible(true);
          setPatientType("insurance");
          toast.success(t("Patient marked as eligible through referral"));
        }
      }
      setEligibilityChecked(true);
    } catch (error) {
      console.error("Referral eligibility check error:", error);
      setEligibilityData(null);
      setReferralEligible(false);
      setPatientType("cash");
      setEligibilityChecked(true);
      toast.error(t("Failed to check referral eligibility. Please try again."));
    } finally {
      setEligibilityLoading(false);
      setOpenReferralModal(false);
    }
  };

  const handleReferralEligibilityOverride = (checked) => {
    if (checked) {
      const patientPlan =
        selectedPayerData ||
        selectedPatientData?.insurance_plans?.find((plan) => plan.is_primary) ||
        selectedPatientData?.insurance_plans?.[0] ||
        null;

      if (patientPlan) {
        setSelectedPayerData(patientPlan);
        setSelectedInsurancePlan(patientPlan);
      }

      setReferralEligible(true);
      setEligibilityChecked(true);
      setPatientType("insurance");
      toast.success(t("Referral eligibility overridden by admin"));
      return;
    }

    setReferralEligible(false);
    toast.success(t("Referral eligibility override removed"));
  };
  useEffect(() => {
    if (preSelectedPatientData) {
      setPatientType(preSelectedPatientData?.beneficiary_type?.toLowerCase());
      if (preSelectedPatientData?.beneficiary_type?.toLowerCase() != "cash") {
        setPatientInsurancePlans([
          ...(preSelectedPatientData?.insurance_plans || []),
          // ...(cashPayers?.length ? [cashPayers[0]] : []),
        ]);
        const firstPlan = preSelectedPatientData?.insurance_plans?.[0];
        const planId = firstPlan?.his_payer_id || firstPlan?.payer_id;
        setSelectedPayerData(firstPlan);
        setSelectedInsurancePlan(firstPlan);

        setPriatitionerPricePayload((prev) => ({
          ...prev,
          payer_id: planId,
          payment_type: preSelectedPatientData?.beneficiary_type?.toLowerCase(),
        }));
      } else {
        setSelectedPayerData(cashPayers?.[0]);
        setSelectedInsurancePlan(null);
        setPriatitionerPricePayload((prev) => ({
          ...prev,
          payer_id: cashPayers?.[0]?.payer_id,
          payment_type: "cash",
        }));
      }
      setSelectedPatient(preSelectedPatientData);
      setSelectedPatientData(preSelectedPatientData);
    }
  }, [preSelectedPatientData]);

  function getSelectedPayerData() {
    if (preSelectedPatientData?.beneficiary_type?.toLowerCase() != "cash") {
      return preSelectedPatientData?.insurance_plans?.[0]?.his_payer_id;
    } else if (
      preSelectedPatientData?.beneficiary_type?.toLowerCase() == "cash"
    ) {
      return cashPayers?.[0]?.payer_id;
    }
  }

  useEffect(() => {
    if (
      selectedPatientData &&
      (!followUpPatientTypes?.includes(patientType) || followUp) &&
      practitionerPriceListPayload?.payment_type === patientType
    ) {
      formRef?.current?.setFieldValue("items", []);
      getPractitionerPriceLists();
    }
  }, [
    practitionerPriceListPayload?.visitType,
    practitionerPriceListPayload?.payer_id,
    practitionerPriceListPayload?.practitioner_id,
    selectedPatientData,
    practitionerData?.practitioner_id,
    followUp,
    patientType,
    eligibilityData,
    referralEligible,
    forceRefreshTrigger,
    isAppointment,
    appointmentId,
  ]);

  useEffect(() => {
    if (practitionersList?.length) {
      const practitioner = practitionersList?.find(
        (data) => data?.practitioner_id == selectedPractitionerId,
      );
      formRef?.current?.setFieldValue(
        "speciality",
        practitioner?.speciality?.name_en,
      );
      setSpeciality(practitioner?.speciality);
      setDepartment(practitioner?.department);
      setPractitionerData(practitioner);
    }
  }, [practitionersList]);

  const getPractitionerPriceLists = useCallback(async () => {
    if (
      practitionerPriceListPayload?.payer_id &&
      practitionerPriceListPayload?.practitioner_id &&
      (patientType === "cash" ||
        patientType === "corporate" ||
        patientType === "government" ||
        eligibilityData ||
        referralEligible) &&
      practitionerPriceListPayload?.payment_type === patientType
    ) {
      if (!isHaveGetPricelistPermission) return false;
      setCheckDataLoading(true);
      const res = await fetchData("POST", `/v3/payer/checkMasterPrice`, {
        payer_id: practitionerPriceListPayload?.payer_id,
      });
      setMasterPriceCheck(res?.is_master_price || false);
      setMasterPriceVat(res?.is_vat_applicateable === true);
      let payload = {
        price_list_type_id: "67a2f54d664671bae04c678c",
        payer_id: practitionerPriceListPayload?.payer_id,
        practitioner_id: practitionerPriceListPayload?.practitioner_id,
        payment_type: practitionerPriceListPayload?.payment_type || "insurance",
        follow_up: followUp?.toLowerCase() === "follow up",
        is_master_price: res?.is_master_price || false,
        department_id: department?.id || practitionerData?.department?.id,
      };
      const isMaster = res?.is_master_price ?? false;
      let response;
      if (isAppointment && appointmentId) {
        response = await getAppointmentItems(
          appointmentId,
          selectedPayerData,
          selectedPatientData,
        );
        if (!response?.length) {
          response = await getPriceListByPractitionerId(
            payload,
            selectedPayerData,
            selectedPatientData,
            i18n,
            followUp,
            isMaster,
            res,
            res?.is_vat_applicateable === true,
          );
        }
      } else {
        response = await getPriceListByPractitionerId(
          payload,
          selectedPayerData,
          selectedPatientData,
          i18n,
          followUp,
          isMaster,
          res,
          res?.is_vat_applicateable === true,
        );
      }
      if (response && response.length) {
        const existingItems = formRef?.current?.values?.items || [];
        const newItems = response.filter((newItem) => {
          return !existingItems.some(
            (existingItem) => existingItem?.item_id == newItem?.item_id,
          );
        });

        if (newItems.length) {
          formRef?.current?.setFieldValue("items", [
            ...existingItems,
            ...newItems,
          ]);
        }
      }
      setCheckDataLoading(false);
    }
  }, [
    practitionerPriceListPayload?.visitType,
    practitionerPriceListPayload?.payer_id,
    practitionerPriceListPayload?.practitioner_id,
    selectedPatientData,
    followUp,
    patientType,
    eligibilityData,
    referralEligible,
    forceRefreshTrigger,
    isAppointment,
    appointmentId,
  ]);

  useEffect(() => {
    const fetchPatientDeposit = async () => {
      setDepositLoading(true);
      try {
        const res = await fetchData("POST", "/v3/deposit/getdeposit", {
          patient_id: selectedPatientData?.patient_id,
        });
        setDepositBalance(res?.balance ?? 0);
        return res;
      } catch (err) {
        console.log(err);
      } finally {
        setDepositLoading(false);
      }
    };
    if (selectedPatientData?.patient_id && openDepositForm == false) {
      fetchPatientDeposit();
    }
  }, [selectedPatientData, openDepositForm, refreshDepositTrigger]);

  useEffect(() => {
    const fetchPatientCredit = async () => {
      setCreditLoading(true);
      try {
        const res = await fetchData(
          "POST",
          "/v3/patient/creditBalance/getByPatientId",
          {
            patient_id: selectedPatientData?.patient_id,
          },
        );
        setCreditBalance(res?.data?.patient_credit_amount ?? 0);
        return res;
      } catch (err) {
        console.log(err);
      } finally {
        setCreditLoading(false);
      }
    };
    if (selectedPatientData?.patient_id) {
      fetchPatientCredit();
    }
  }, [selectedPatientData, refreshCreditTrigger]);

  const getMinutesFromTime = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const getDocumentIdForPatient = (patient = selectedPatientData) => {
    return (
      patient?.document_id ||
      formRef?.current?.values?.patientDocumentId ||
      patientDoc ||
      ""
    );
  };

  const isSlotValidForPatient = (slot, patient = selectedPatientData) => {
    const selectedDocumentId = getDocumentIdForPatient(patient);

    if (!slot?.is_appointment || !selectedDocumentId) return true;
    return slot?.document_id?.toString() === selectedDocumentId?.toString();
  };

  const setSelectedSlotData = (slot) => {
    setDay(slot?.day);
    setStartTime(slot?.start_time);
    setEndTime(slot?.end_time);
    setIsAppointment(slot?.is_appointment);
    setAppointmentId(slot?.appointment_id);
  };

  const clearSelectedSlotData = () => {
    setDay(null);
    setStartTime(null);
    setEndTime(null);
    setIsAppointment(false);
    setAppointmentId(null);
  };

  const getNearestValidSlot = (slots, patient = selectedPatientData) => {
    if (!Array.isArray(slots) || slots.length === 0) return null;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const selectedDocumentId = getDocumentIdForPatient(patient);

    const slotsByDistance = slots
      .map((slot) => {
        const slotMinutes = getMinutesFromTime(slot?.start_time);
        if (slotMinutes === null) return null;

        return {
          slot,
          slotMinutes,
          difference: Math.abs(slotMinutes - currentMinutes),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.difference !== b.difference) {
          return a.difference - b.difference;
        }

        const aIsFuture = a.slotMinutes >= currentMinutes;
        const bIsFuture = b.slotMinutes >= currentMinutes;
        if (aIsFuture !== bIsFuture) return aIsFuture ? -1 : 1;

        return a.slotMinutes - b.slotMinutes;
      });

    const selectedPatientAppointment = selectedDocumentId
      ? slotsByDistance.find(({ slot }) => {
          return (
            slot?.is_appointment &&
            slot?.document_id?.toString() === selectedDocumentId?.toString()
          );
        })
      : null;

    if (selectedPatientAppointment?.slot)
      return selectedPatientAppointment.slot;

    return slotsByDistance.filter(({ slot }) => {
      return isSlotValidForPatient(slot, patient);
    })?.[0]?.slot;
  };

  useEffect(() => {
    if (
      !Array.isArray(slotsData) ||
      !slotsData.length ||
      !selectedPatientData
    ) {
      return;
    }

    const preferredSlot = getNearestValidSlot(slotsData);
    if (preferredSlot) {
      const isPreferredSlotSelected =
        preferredSlot?.start_time === startTime &&
        preferredSlot?.end_time === endTime;

      if (!isPreferredSlotSelected) {
        setSelectedSlotData(preferredSlot);
      }
    } else {
      clearSelectedSlotData();
      toast.error(t("No valid slots available for selected patient"));
    }
  }, [selectedPatientData?.document_id, slotsData]);

  const getSlotsData = async () => {
    if (practitionerData == null) return;
    let day = moment(visitDate).format("dddd");

    let payload = {
      practitioner_id: practitionerData?.practitioner_id,
      start_date: moment(visitDate).format("YYYY-MM-DD"),
      end_date: moment(visitDate).format("YYYY-MM-DD"),
      day,
    };

    let response = await fetchData(
      "POST",
      "/v3/visit/getPractitionerSlots",
      payload,
    );
    if (response?.available_slots) {
      const availableSlots = response?.available_slots;
      const selectedSlot = getNearestValidSlot(availableSlots);

      setSlotData(availableSlots);

      if (!selectedSlot) {
        clearSelectedSlotData();
        toast.error(t("No valid slots available for selected patient"));
        return;
      }

      setSelectedSlotData(selectedSlot);
      let slotPatientData = null;
      if (selectedSlot?.patient_id) {
        setFollowUp(null);
        const res = await fetchData("POST", "/v3/patient/getById", {
          patient_id: selectedSlot?.patient_id,
        });
        if (res?.data) {
          slotPatientData = res.data;
          handlePatientSelect(res?.data, formRef.current, "patientName", true);
        }
      }
      const patientToCheck = slotPatientData || selectedPatientData;
      const patientTypeToCheck =
        slotPatientData?.beneficiary_type?.toLowerCase() || patientType;
      const shouldWaitForEligibility =
        patientTypeToCheck === "insurance" && !eligibilityChecked;

      if (patientToCheck && !shouldWaitForEligibility) {
        await checkFollowUp(patientToCheck, patientTypeToCheck);
      }
    } else {
      setSlotData(null);
      toast.error(
        "Selected date has no available appointments for this practitioner",
      );
    }
  };
  useEffect(() => {
    if (practitionerData) {
      getSlotsData();
    }
  }, [practitionerData, visitDate]);

  useEffect(() => {
    if (
      selectedPayerData &&
      selectedPatientData &&
      selectedPayerData?.payer_id !== "PAY-0003" &&
      !eligibilityChecked
    ) {
      if (selectedPatientData?.beneficiary_type?.toLowerCase() == "insurance") {
        handleCheckElibility();
      }
    }
  }, [selectedPatientData]);

  const getPatientByDocId = async (docId, props) => {
    setSearchLoading(true);
    try {
      const res = await fetchData("POST", "v3/patient/filter", {
        document_id: docId,
      });
      if (res?.data?.length === 1) {
        handlePatientSelect(res?.data?.[0], props, "patientName");
      } else {
        toast.error(t("PatientNotFound"));
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="w-full h-full">
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={createVisitValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {(props) => (
          <>
            <div className="w-full mb-2">
              {(patientType === "insurance" ||
                ((followUpPractitionerName || followUpSpecialityName) &&
                  (followUp === "Follow Up" || followUp === "Follow up"))) && (
                <div className="mb-1 flex flex-wrap items-center justify-end gap-2">
                  {(followUpPractitionerName || followUpSpecialityName) &&
                    (followUp === "Follow Up" || followUp === "Follow up") && (
                      <div className="inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 shadow-sm">
                        <span className="text-xs font-bold uppercase tracking-wide text-red-600">
                          {t("Follow Up From Previous Visit")}
                        </span>
                        {followUpPractitionerName && (
                          <span className="text-sm font-semibold text-gray-900">
                            <span className="mr-1 text-xs font-bold uppercase text-gray-600">
                              {t("Practitioner")}:
                            </span>
                            {followUpPractitionerName}
                          </span>
                        )}
                        {followUpSpecialityName && (
                          <span className="text-sm font-semibold text-gray-900">
                            <span className="mr-1 text-xs font-bold uppercase text-gray-600">
                              {t("Speciality")}:
                            </span>
                            {followUpSpecialityName}
                          </span>
                        )}
                      </div>
                    )}
                  {patientType === "insurance" && (
                    <RemMaxLimit
                      remainingMaxLimit={episodeRemainingLimit}
                      maxLimit={episodeMaxLimit}
                    />
                  )}
                </div>
              )}
              <BillSummary
                className="w-full"
                patientType={patientType}
                formikRef={formRef}
                selectedPatientData={selectedPatientData}
                selectedPayerData={selectedPayerData}
                cardOptions={cardOptions}
                depositBalance={depositBalance}
                updateDepositValues={true}
                invoiceDiscount={invoiceDiscount}
                followUp={followUp}
                episodeRemainingLimit={episodeRemainingLimit}
                episodeMaxLimit={episodeMaxLimit}
              />
              <div className="bg-gray-300 p-1 rounded-b-md border border-blue-100/50">
                <div>
                  <label className="input-label block !mt-0 !mb-1.5 font-bold text-blue-800">
                    {t("DoctorsOrder")} :
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <TextInput
                    parentClass="!mb-0"
                    useFormik={false}
                    disabled={isLoading || searchLoading}
                    placeholder={t("SearchPatientByMRN")}
                    onChange={(e) => {
                      setSelctedPatient(e.target.value);
                      setPatientDoc("");
                    }}
                    value={selctedPatient}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        if (searchLoading) return;
                        e.preventDefault();
                        setSearchLoading(true);
                        try {
                          const res = await fetchData(
                            "POST",
                            "v3/patient/filter",
                            {
                              file_no: selctedPatient,
                            },
                          );
                          if (res?.data?.length === 1) {
                            if (handlePatientSelectMain) {
                              handlePatientSelectMain(res?.data?.[0]);
                            } else {
                              handlePatientSelect(
                                res?.data?.[0],
                                props,
                                "patientFileNo",
                              );
                            }
                          } else {
                            toast.error(t("PatientNotFound"));
                          }
                        } catch (err) {
                          console.log(err);
                        } finally {
                          setSearchLoading(false);
                        }
                      }
                    }}
                  />
                  <TextInput
                    parentClass="!mb-0"
                    useFormik={false}
                    disabled={isLoading || searchLoading}
                    placeholder={t("SearchPatientByDocumentId")}
                    onChange={(e) => {
                      setPatientDoc(e.target.value);
                      setSelctedPatient("");
                    }}
                    value={patientDoc}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        if (searchLoading) return;
                        e.preventDefault();
                        setSearchLoading(true);
                        try {
                          const res = await fetchData(
                            "POST",
                            "v3/patient/filter",
                            {
                              document_id: patientDoc,
                            },
                          );
                          if (res?.data?.length === 1) {
                            if (handlePatientSelectMain) {
                              handlePatientSelectMain(res?.data?.[0]);
                            } else {
                              handlePatientSelect(
                                res?.data?.[0],
                                props,
                                "patientDocumentId",
                              );
                            }
                          } else {
                            toast.error(t("PatientNotFound"));
                          }
                        } catch (err) {
                          console.log(err);
                        } finally {
                          setSearchLoading(false);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <Form className="w-full">
              <PatientAlertsDisplay
                patientId={selectedPatientData?.patient_id}
              />

              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 lg:col-span-11">
                  <div className="grid grid-cols-12 gap-3 items-end mb-2">
                    <div className="col-span-12 lg:col-span-4">
                      <label className="input-label block mb-1">
                        {t("PatientType")}
                      </label>
                      <div className="w-full overflow-x-auto scrollbar-hide">
                        <SwitchButtons
                          isDisabled={
                            selectedPatientData?.beneficiary_type?.toLowerCase() ==
                              "cash" ||
                            (!referralEligible &&
                              (eligibilityData?.nphies_status == "error" ||
                                eligibilityData?.nphies_entries?.CovElgResp?.resource?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.code?.toLowerCase() ===
                                  "eligible") &&
                              !isAdmin)
                          }
                          labels={[
                            { name: t("Cash"), value: "cash" },
                            {
                              name: t("Insurance"),
                              value: "insurance",
                              disabled:
                                selectedPatientData?.beneficiary_type?.toLowerCase() !==
                                "insurance",
                            },
                            {
                              name: t("Corporate"),
                              value: "corporate",
                              disabled:
                                selectedPatientData?.beneficiary_type?.toLowerCase() !==
                                "corporate",
                            },
                            {
                              name: t("Government"),
                              value: "government",
                              disabled:
                                selectedPatientData?.beneficiary_type?.toLowerCase() !==
                                "government",
                            },
                          ]}
                          setActiveState={setPatientType}
                          activeState={patientType}
                        />
                      </div>
                    </div>
                    <div className="col-span-4 lg:col-span-3 flex items-center gap-3">
                      <div className="w-full">
                        <div className="flex items-center justify-between gap-2 text-[13px] md:text-[15px] font-bold text-red-800 bg-red-100 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap shadow-sm h-[30px] w-full">
                          <span className="font-semibold text-[11px] md:text-[14px] uppercase tracking-tight">
                            {t("C.B")}:
                          </span>
                          <span className="tabular-nums">
                            {creditBalance?.toFixed(2) || "0.00"}
                          </span>
                        </div>
                      </div>
                      <div className="w-full">
                        <div className="flex items-center justify-between gap-2 text-[13px] md:text-[15px] font-bold text-indigo-800 bg-indigo-100 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap shadow-sm h-[30px] w-full">
                          <span className="font-semibold text-[11px] md:text-[14px] uppercase tracking-tight">
                            {t("D.B")}:
                          </span>
                          <span className="tabular-nums">
                            {depositBalance?.toFixed(2) || "0.00"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-4 lg:col-span-1">
                      <div className="flex items-center justify-between gap-2 text-[13px] md:text-[15px] font-bold text-success bg-success/10 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap shadow-sm h-[30px] w-full">
                        <span className="font-semibold text-[11px] md:text-[14px] tracking-tight">
                          {t("Age")}:
                        </span>
                        <span className="tabular-nums">
                          {selectedPatientData
                            ? CalculateAge(selectedPatientData?.dob)
                            : "--"}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-12 lg:col-span-2 flex items-end justify-center">
                      <EligibilityDataComponent
                        eligibilityData={eligibilityData}
                        referralEligible={referralEligible}
                      />
                    </div>
                    <div className="col-span-12 lg:col-span-2">
                      <DatePicker
                        name="visit_date"
                        className="w-full"
                        placeholder="Select Date"
                        label={t("Visit Date")}
                        onChange={async (e) => {
                          setVisitDate(e?.target?.value);
                          props.setFieldValue("visit_date", e?.target?.value);
                        }}
                      />
                    </div>
                  </div>

                  {(referralEligible ||
                    (selectedPatientData?.beneficiary_type?.toLowerCase() ===
                      "insurance" &&
                      props.values.auth_ref &&
                      props.values.valid_until)) && (
                    <ReferralEligibilitySummary
                      authRef={props.values.auth_ref}
                      validUntil={props.values.valid_until}
                      isEligible={referralEligible}
                      rightAction={
                        selectedPatientData?.beneficiary_type?.toLowerCase() ===
                          "insurance" &&
                        props.values.auth_ref &&
                        props.values.valid_until ? (
                          <SwitchInput
                            parentClass="!mb-0"
                            label={t("Override Referral Eligibility")}
                            labelClassName="input-label !text-[11px] !mb-0"
                            value={referralEligible}
                            handleChange={(e) =>
                              handleReferralEligibilityOverride(e.value)
                            }
                          />
                        ) : null
                      }
                    />
                  )}

                  <div className="grid grid-cols-12 gap-3 ">
                    <DropdownField
                      parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                      label={t("Act Priority")}
                      name="priority"
                      options={actPriorityOptions(t)}
                      placeholder={t("SelectPriority")}
                    />
                    <TextInput
                      parentClass="col-span-12 md:col-span-8 lg:col-span-4 xl:col-span-4"
                      label={t("SelectPatient")}
                      disabled={searchLoading || isAppointment}
                      placeholder={t("Search By Patient Name")}
                      name="patientName"
                      NavigationIcon={() => (
                        <div className=" flex gap-2 items-center justify-center cursor-pointer">
                          {selectedPatientData?.nationality && (
                            <Flag
                              code={getCountryCode(
                                selectedPatientData?.nationality || "Saudi",
                              )}
                              fallback={<span>{t("Unknown")}</span>}
                              className="h-4 w-6"
                              title={selectedPatientData?.nationality}
                            />
                          )}
                          <FaPlusCircle
                            size={20}
                            title="Create Beneficiary"
                            className="text-iconC"
                            onClick={() => {
                              navigate("/create-beneficiary", {
                                state: {
                                  fromVisit: true,
                                  returnDetail: {
                                    day: slotDay,
                                    start_time: startTime,
                                    end_time: endTime,
                                    date,
                                    selectedPractitionerId,
                                    appointment_id,
                                    tab: state?.tab,
                                    is_without_slot: true,
                                  },
                                },
                              });
                            }}
                          />

                          <RiEditCircleFill
                            size={20}
                            title="Edit Beneficiary"
                            style={{
                              color: props.values?.patient_id
                                ? "#0070c2"
                                : "#ccc",
                              cursor: props.values?.patient_id
                                ? "pointer"
                                : "not-allowed",
                            }}
                            onClick={() => {
                              if (!props?.values.patient_id) {
                                toast.error(t("Please select a patient first"));
                                return;
                              }
                              navigate("/edit-beneficiary", {
                                state: {
                                  beneficiaryDetails: selectedPatientData,
                                  fromVisit: true,
                                  returnDetail: {
                                    day: slotDay,
                                    start_time: startTime,
                                    end_time: endTime,
                                    selectedPractitionerId,
                                    appointment_id,
                                    tab: state?.tab,
                                    doc_id: selectedPatientData?.document_id,
                                    is_without_slot: true,
                                  },
                                },
                              });
                            }}
                          />
                        </div>
                      )}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          if (searchLoading) return;
                          if (!props.values?.patientName) {
                            toast.error(t("Please enter name"));
                            return;
                          }
                          e.preventDefault();
                          setSearchLoading(true);
                          try {
                            const res = await fetchData(
                              "POST",
                              "v3/patient/filter",
                              {
                                full_name: props?.values?.patientName,
                              },
                            );
                            if (res?.data?.length > 0) {
                              if (res?.data?.length === 1) {
                                handlePatientSelect(
                                  res?.data?.[0],
                                  props,
                                  "patientName",
                                );
                              } else {
                                setListModalData(res?.data);
                                setOpenListModal(true);
                              }
                            } else {
                              toast.error(t("PatientNotFound"));
                            }
                          } catch (err) {
                            console.log(err);
                          } finally {
                            setSearchLoading(false);
                          }
                        }
                      }}
                      value={props?.values?.patientName}
                    />
                    <TextInput
                      parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                      label={t("MRN")}
                      placeholder={t("SearchPatientByMRN")}
                      disabled={searchLoading || isAppointment}
                      name="patientFileNo"
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          if (searchLoading) return;
                          if (!props.values?.patientFileNo) {
                            toast.error(t("Please enter file number"));
                            return;
                          }
                          e.preventDefault();
                          setSearchLoading(true);
                          try {
                            const res = await fetchData(
                              "POST",
                              "v3/patient/filter",
                              {
                                file_no: props?.values?.patientFileNo,
                              },
                            );
                            if (res?.data?.length === 1) {
                              handlePatientSelect(
                                res?.data?.[0],
                                props,
                                "patientFileNo",
                              );
                            } else {
                              toast.error(t("PatientNotFound"));
                            }
                          } catch (err) {
                            console.log(err);
                          } finally {
                            setSearchLoading(false);
                          }
                        }
                      }}
                    />
                    <TextInput
                      value={props?.values?.patientDocumentId}
                      parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                      label={t("DocumentId")}
                      placeholder={t("Search By Doc ID")}
                      disabled={searchLoading || isAppointment}
                      name="patientDocumentId"
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          if (searchLoading) return;
                          if (!props.values?.patientDocumentId) {
                            toast.error(t("Please enter document id"));
                            return;
                          }
                          e.preventDefault();
                          await getPatientByDocId(
                            props?.values?.patientDocumentId,
                            props,
                          );
                        }
                      }}
                    />
                    <TextInput
                      parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                      label={t("PatientMobile")}
                      disabled={searchLoading || isAppointment}
                      placeholder={`${t("SearchPatientByMobile")} No`}
                      name="contactNo"
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          if (searchLoading) return;
                          if (!props.values?.contactNo) {
                            toast.error(t("Please enter contact no"));
                            return;
                          }
                          e.preventDefault();
                          setSearchLoading(true);
                          try {
                            const res = await fetchData(
                              "POST",
                              "v3/patient/filter",
                              {
                                contact: props?.values?.contactNo,
                                ...contactPagination,
                              },
                            );
                            if (res?.data?.length > 0) {
                              if (res?.data?.length === 1) {
                                handlePatientSelect(
                                  res?.data?.[0],
                                  props,
                                  "contactNo",
                                );
                              } else {
                                setListModalData(res);
                                setOpenListModal(true);
                              }
                            } else {
                              toast.error(t("PatientNotFound"));
                            }
                          } catch (err) {
                            console.log(err);
                          } finally {
                            setSearchLoading(false);
                          }
                        }
                      }}
                    />
                    <DropdownField
                      // disabled={isReceptionist && businessId=='29'}
                      parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                      label={t("VisitType")}
                      placeholder={t("SelectVisitType")}
                      name="visitType"
                      options={[
                        ...visitTypeOptions(t),
                        { label: t("Corporate"), value: "corporate" },
                        { label: t("Government"), value: "government" },
                      ]}
                      onChange={(event) => handleChangeVisitType(event, props)}
                    />
                    {(() => {
                      const payerOptions =
                        props.values.patientType == "cash"
                          ? patientCashPlansOptionsFormat(cashPayers)
                          : patientInsuracnePlansOptionsFormat(
                              patientInsuracnePlans,
                            );

                      const selectedPayerLabel = props.values.payerName
                        ? payerOptions.find(
                            (o) => o.value == props.values.payerName,
                          )?.label || t("SelectPayer")
                        : t("No payer selected");

                      return (
                        <div
                          className="col-span-12 md:col-span-6 lg:col-span-3 xl:col-span-3"
                          title={selectedPayerLabel}
                        >
                          <DropdownField
                            key={props.values.payerName || "empty"}
                            value={props?.values?.payerName}
                            label={t("SelectPayer")}
                            placeholder={t("SelectPayer")}
                            name="payerName"
                            optionLabel="label"
                            optionValue="value"
                            options={payerOptions}
                            onChange={(event) => {
                              let value = event?.target?.value;
                              if (props?.values?.patientType == "cash") {
                                let payerData = cashPayers?.find(
                                  (data) => data?.payer_id == value,
                                );
                                props.setFieldValue("payerName", value);
                                props.setFieldValue("items", []);
                                setSelectedPayerData(payerData);
                                setPriatitionerPricePayload((prev) => ({
                                  ...prev,
                                  payer_id: value,
                                  payment_type: "cash",
                                }));
                              } else {
                                let payerData = patientInsuracnePlans?.find(
                                  (data) =>
                                    data?.his_payer_id == value ||
                                    data?.payer_id == value,
                                );
                                props.setFieldValue("payerName", value);
                                props.setFieldValue("items", []);
                                setSelectedPayerData(payerData);
                                setPriatitionerPricePayload((prev) => ({
                                  ...prev,
                                  payer_id: value,
                                  payment_type: props?.values?.patientType,
                                }));
                                if (
                                  props?.values?.patientType === "insurance"
                                ) {
                                  handleCheckElibility(payerData);
                                }
                              }
                              if (setEligibilityData) {
                                setEligibilityData(null);
                              }
                            }}
                          />
                        </div>
                      );
                    })()}

                    <DropdownField
                      loading={fieldsLoading}
                      parentClass="col-span-12 md:col-span-6 lg:col-span-3 xl:col-span-3"
                      label={t("SelectPractitioner")}
                      placeholder={t("SelectPractitioner")}
                      name="practitioner"
                      filter
                      options={practionerOptionsFormat(practitionersList)}
                      onChange={(event) =>
                        handleChangePractitioner(event, props)
                      }
                    />

                    {!props?.values?.deduct_from_deposit && (
                      <DropdownField
                        parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                        label={t("PaymentMethod")}
                        placeholder={t("SelectPaymentMethod")}
                        name="paymentMethod"
                        options={paymentMethods(t)}
                        onChange={(e) => {
                          const method = e.value;
                          const currentCardKey =
                            props.values.cardType || "Mada";
                          const allCardKeys = cardOptions.map(
                            (option) => option.value,
                          );
                          props.setFieldValue("paymentMethod", method);

                          if (method === "card") {
                            const cleanedValues = { ...props.values };
                            allCardKeys.forEach((key) => {
                              if (key !== currentCardKey)
                                delete cleanedValues[key];
                            });
                            props.setValues({
                              ...cleanedValues,
                              paymentMethod: method,
                              cardType: currentCardKey,
                              cash: undefined,
                              bank: undefined,
                              credit: undefined,
                              cards: undefined,
                            });
                          } else if (method === "multi_payment") {
                            const cleanedValues = { ...props.values };
                            allCardKeys.forEach((key) => {
                              if (key !== currentCardKey)
                                delete cleanedValues[key];
                            });
                            props.setValues({
                              ...cleanedValues,
                              paymentMethod: method,
                              cardType: cleanedValues.cardType || "Mada",
                              cash: cleanedValues.cash ?? 0,
                              bank: cleanedValues.bank ?? 0,
                              credit: cleanedValues.credit ?? 0,
                              [currentCardKey]:
                                cleanedValues[currentCardKey] ?? 0,
                              cards:
                                props.values.cards &&
                                props.values.cards.length > 0
                                  ? props.values.cards
                                  : [{ cardType: "Mada", amount: 0 }],
                            });
                          } else {
                            const cleanedValues = { ...props.values };
                            allCardKeys.forEach((key) => {
                              delete cleanedValues[key];
                            });
                            props.setValues({
                              ...cleanedValues,
                              paymentMethod: method,
                              cardType: undefined,
                              cash: undefined,
                              bank: undefined,
                              credit: undefined,
                            });
                          }
                        }}
                      />
                    )}

                    {(props?.values?.paymentMethod === "card" ||
                      props?.values?.paymentMethod === "multi_payment") && (
                      <DropdownField
                        parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                        label={
                          props?.values?.paymentMethod === "multi_payment"
                            ? t("Add Card")
                            : t("Card Type")
                        }
                        placeholder={t("SelectCardType")}
                        name="cardType"
                        value={
                          props?.values?.paymentMethod === "multi_payment"
                            ? null
                            : props.values.cardType
                        }
                        options={
                          props?.values?.paymentMethod === "multi_payment"
                            ? cardOptions
                                .map((group) => ({
                                  ...group,
                                  options: group?.options?.filter(
                                    (opt) =>
                                      !props.values.cards?.some(
                                        (card) => card.cardType === opt.value,
                                      ),
                                  ),
                                }))
                                .filter(
                                  (group) =>
                                    group.options && group.options.length > 0,
                                )
                            : cardOptions
                        }
                        onChange={(e) => {
                          const newCardType = e.value;
                          if (props.values.paymentMethod === "multi_payment") {
                            if (newCardType) {
                              const newCards = [
                                ...(props.values.cards || []),
                                { cardType: newCardType, amount: 0 },
                              ];
                              props.setFieldValue("cards", newCards);
                              setTimeout(
                                () => props.setFieldValue("cardType", null),
                                0,
                              );
                            }
                          } else {
                            props.setFieldValue("cardType", newCardType);
                          }
                        }}
                      />
                    )}

                    {props?.values?.paymentMethod === "multi_payment" && (
                      <>
                        <TextInput
                          parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                          name="cash"
                          label={t("Cash")}
                          placeholder={t("EnterCashAmount")}
                          type="number"
                          step="any"
                        />
                        <TextInput
                          parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                          name="bank"
                          label={t("Bank")}
                          placeholder={t("EnterBankAmount")}
                          type="number"
                          step="any"
                        />
                        <TextInput
                          parentClass="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2"
                          name="credit"
                          label={t("Credit")}
                          placeholder={t("EnterCreditAmount")}
                          type="number"
                          step="any"
                        />
                        {props.values.cards &&
                          props.values.cards.map((card, index) => (
                            <div
                              key={index}
                              className="col-span-12 md:col-span-6 lg:col-span-2 xl:col-span-2 flex items-end gap-2"
                            >
                              <div className="flex-1">
                                <TextInput
                                  name={`cards.${index}.amount`}
                                  label={
                                    cardLabels[card.cardType] || card.cardType
                                  }
                                  placeholder={t("Amount")}
                                  type="number"
                                  step="any"
                                />
                              </div>
                              <div className="mb-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newCards = props.values.cards.filter(
                                      (_, i) => i !== index,
                                    );
                                    props.setFieldValue("cards", newCards);
                                  }}
                                  className="text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                                >
                                  <FaTrash size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                      </>
                    )}

                    {depositBalance > 0 && (
                      <SwitchInput
                        name="deduct_from_deposit"
                        value={props?.values?.deduct_from_deposit}
                        label={t("Deduct From Deposit")}
                        handleChange={(e) => {
                          props.setFieldValue("deduct_from_deposit", e.value);
                          if (e.value) {
                            props.setFieldValue("paymentMethod", "Deposit");
                          } else {
                            props.setFieldValue("paymentMethod", "card");
                            props.setFieldValue("cardType", "Mada");
                            props.setFieldValue("cards", []);
                          }
                        }}
                        parentClass="col-span-12 md:col-span-6 justify-between lg:col-span-3 xl:col-span-2"
                      />
                    )}

                    {showComments && (
                      <TextInput
                        className="h-40"
                        parentClass="col-span-12"
                        label={t("Comment")}
                        placeholder={t("AddComment")}
                        name="comment"
                      />
                    )}

                    <div className="col-span-12 grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-12">
                        <div
                          ref={slotsContainerRef}
                          className="flex w-full gap-x-2 overflow-x-auto pb-2 no-scrollbar"
                        >
                          {slotsData?.length > 0 ? (
                            slotsData.map((slot, idx) => {
                              const isActive =
                                slot?.start_time === startTime &&
                                slot?.end_time === endTime;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  data-slot-key={`${slot?.start_time}-${slot?.end_time}`}
                                  onClick={async () => {
                                    setIsLoading(true);
                                    try {
                                      setDay(slot?.day);
                                      setStartTime(slot?.start_time);
                                      setEndTime(slot?.end_time);
                                      setIsAppointment(slot?.is_appointment);
                                      setAppointmentId(slot?.appointment_id);
                                      if (slot?.is_appointment) {
                                        setFollowUp(null);
                                        const res = await fetchData(
                                          "POST",
                                          "/v3/patient/getById",
                                          { patient_id: slot?.patient_id },
                                        );
                                        if (res?.data) {
                                          handlePatientSelect(
                                            res?.data,
                                            props,
                                            "patientName",
                                            true,
                                          );
                                        }
                                      }
                                    } catch (err) {
                                      console.log(err);
                                    } finally {
                                      setIsLoading(false);
                                    }
                                  }}
                                  className={`flex items-center whitespace-nowrap border rounded-md py-1.5 px-3 w-fit text-xs font-medium transition-all duration-200
                                    ${
                                      isActive
                                        ? slot?.is_appointment
                                          ? "bg-green-500 border-green-600 text-white shadow-md"
                                          : "bg-primary border-primary text-white shadow-md"
                                        : slot?.is_appointment
                                          ? "border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                                          : "border-primary text-primary hover:bg-primary hover:text-white"
                                    }`}
                                >
                                  <IoTimeOutline
                                    size={14}
                                    className={`mr-2 transition-all duration-200 ${
                                      isActive
                                        ? "text-white"
                                        : slot?.is_appointment
                                          ? "text-green-600"
                                          : "text-primary"
                                    }`}
                                  />
                                  {slot?.is_appointment && (
                                    <>
                                      {slot?.patient_name}({slot?.document_id})
                                    </>
                                  )}
                                  {slot?.is_appointment && "--"}{" "}
                                  {slot?.start_time} - {slot?.end_time}
                                </button>
                              );
                            })
                          ) : (
                            <p className="text-gray-400 text-sm italic">
                              {t("No slots available")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 w-full md:col-span-2 lg:col-span-1 xl:col-span-1 flex flex-row justify-end items-start gap-2">
                  <div className="flex flex-col gap-2 w-fit">
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      buttonType="primary"
                      icon={<FaHistory size={16} />}
                      title={t("BillingHistory")}
                      onClick={() => {
                        if (selectedPatient) {
                          navigate(
                            `/patient-billing-history/${selectedPatient?.patient_id}`,
                            {
                              state: {
                                selectedPatient: selectedPatient,
                                backRoute: "/visits",
                              },
                            },
                          );
                        } else {
                          toast.error(
                            "Please select patient to view billing history",
                          );
                        }
                      }}
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      buttonType="danger"
                      icon={<FaCreditCard size={16} />}
                      title={t("Pay Credit")}
                      onClick={() => {
                        if (selectedPatient) {
                          setOpenPayCreditForm(true);
                        } else {
                          toast.error("Please select patient to pay credit");
                        }
                      }}
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      buttonType="success"
                      icon={<FaWallet size={16} />}
                      title={t("Add Deposit")}
                      onClick={() => {
                        if (selectedPatient) {
                          setOpenDepositForm(true);
                        } else {
                          toast.error("Please select patient to deposit");
                        }
                      }}
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      disabled={!selectedPatient && !selectedPatientData}
                      onClick={() => setOpenReferralModal(true)}
                      title={t("Add Referral")}
                      icon={<FaPlusCircle size={18} />}
                      buttonType="primary"
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      buttonType="warning"
                      icon={<FaHandHoldingUsd size={16} />}
                      title={t("Withdraw Deposit")}
                      onClick={() => {
                        if (selectedPatient) {
                          setOpenWithdrawDepositForm(true);
                        } else {
                          toast.error(
                            "Please select patient to withdraw deposit",
                          );
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-2 w-fit">
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      buttonType="secondary"
                      icon={<FaCommentAlt size={16} />}
                      title={t("Comments")}
                      onClick={() => setShowComments((prev) => !prev)}
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      disabled={
                        eligibilityLoading ||
                        isLoading ||
                        props?.values?.patientType !== "insurance"
                      }
                      onClick={() => handleCheckElibility(selectedPayerData)}
                      title={t("check_eligibility")}
                      loading={eligibilityLoading}
                      icon={<HiShieldCheck size={18} />}
                      buttonType="success"
                      showLoadingText={false}
                    />
                    <CustomButton
                      className="!px-1.5 !w-fit flex justify-center items-center"
                      pt={{ icon: { className: "!mx-0" } }}
                      onClick={() => {
                        const currentItems =
                          formRef?.current?.values?.items || [];
                        if (currentItems.length === 0) {
                          toast.error(t("No items to apply discount"));
                          return;
                        }
                        setOpenDiscountModal(true);
                      }}
                      title={t("Apply Discount to All Items")}
                      icon={<RiDiscountPercentLine size={18} />}
                      buttonType="primary"
                    />
                    {/* <CustomButton
                      className='!px-1.5 !w-fit flex justify-center items-center'
                      pt={{ icon: { className: '!mx-0' } }}
                      buttonType='primary'
                      icon={<FaSearch size={16} />}
                      title={t('DoctorsOrder')}
                      onClick={() => setOpenVisit(true)}
                    /> */}
                  </div>
                </div>
              </div>
              <div className="col-span-12">
                {isHaveGetPricelistPermission && (
                  <VisitPriceItems
                    masterPriceCheck={masterPriceCheck}
                    masterPriceVat={masterPriceVat}
                    checkDataLoading={checkDataLoading}
                    formikRef={formRef}
                    formValues={formRef?.current?.values}
                    selectedPayerData={selectedPayerData}
                    department_id={
                      practitionerData?.department?.id || department?.id
                    }
                    selectedPatientData={memoizedPatientData}
                    isShowTitle={false}
                    showSearch={true}
                    showTable={true}
                  />
                )}
              </div>
              <div className="flex items-center justify-end mt-3 gap-3">
                {hasPermission("visit", "create") && (
                  <CustomButton
                    disabled={
                      eligibilityLoading ||
                      isLoading ||
                      depositLoading ||
                      checkDataLoading
                    }
                    type="submit"
                    label={t("CreateVisit")}
                    loading={isLoading}
                    icon={<FaCheckDouble size={12} className="me-2" />}
                    buttonType="primary"
                  />
                )}
              </div>
            </Form>

            <ReusableModal
              isVisible={openListModal}
              title={t("Patient List")}
              HeaderIcon={FaUser}
              onClose={() => {
                setOpenListModal(false);
                setListModalData([]);
                setContactPagination({ page: 1, perPage: 10 });
              }}
              position="top"
            >
              <PatientListModal
                data={listModalData}
                payload={contactPagination}
                setPayloadData={setContactPagination}
                handlePatientSelect={handlePatientSelect}
                props={props}
                setListModalData={setListModalData}
                setOpenListModal={setOpenListModal}
              />
            </ReusableModal>
            <ApplyDiscountModal
              isVisible={openDiscountModal}
              onClose={() => setOpenDiscountModal(false)}
              formikRef={formRef}
              setInvoiceDiscount={setInvoiceDiscount}
              invoiceDiscount={invoiceDiscount}
              departmentId={practitionerData?.department?.id}
            />
            <AddReferralModal
              isVisible={openReferralModal}
              onClose={() => setOpenReferralModal(false)}
              onSubmit={handleAddReferral}
              initialValues={{
                auth_ref: props.values.auth_ref,
                valid_until: props.values.valid_until,
              }}
            />
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateVisitWithoutSlot;
